import * as ClipperLib from "clipper-lib";
import * as THREE from "three";
import { signedArea } from "./signedArea";

// clipper-lib works on integer coordinates — scale up before offsetting, down afterwards.
const COORDINATE_SCALE = 1e5;
const MITER_LIMIT = 2;
const MINIMUM_RING_LENGTH = 3;

/**
 * Robust ring offset via clipper-lib. A naive per-edge construction self-intersects, inverts and
 * throws vertices into far spikes for large inward distances; clipper resolves those topology
 * changes and we keep the largest resulting ring.
 *
 * `+distance` always shrinks the shape and `-distance` always grows it: the winding is normalised
 * before offsetting and restored afterwards, so the result does not depend on the order the
 * vertices were drawn in.
 *
 * Returns null when the inset collapsed the shape entirely.
 */
function offsetPolygon({ points, distance }: { points: ReadonlyArray<THREE.Vector3>; distance: number }): Array<THREE.Vector3> | null {
	if (points.length < MINIMUM_RING_LENGTH) {
		return null;
	}

	const height = points.reduce((sum, point) => sum + point.y, 0) / points.length;
	const isClockwise = signedArea({ points }) < 0;

	const path: ClipperLib.Path = points.map((point) => ({ X: point.x, Y: point.z }));

	// clipper grows a positively oriented path on a positive delta, so normalise the winding first.
	if (isClockwise) {
		path.reverse();
	}

	ClipperLib.JS.ScaleUpPath(path, COORDINATE_SCALE);

	const clipperOffset = new ClipperLib.ClipperOffset(MITER_LIMIT);
	clipperOffset.AddPath(path, ClipperLib.JoinType.jtMiter, ClipperLib.EndType.etClosedPolygon);

	const solution: ClipperLib.Paths = [];

	// Negated because our convention is inward-positive while clipper's delta is outward-positive.
	clipperOffset.Execute(solution, -distance * COORDINATE_SCALE);

	if (solution.length === 0) {
		return null;
	}

	const largestRing = solution.reduce((largest, ring) =>
		Math.abs(ClipperLib.Clipper.Area(ring)) > Math.abs(ClipperLib.Clipper.Area(largest)) ? ring : largest,
	);

	ClipperLib.JS.ScaleDownPath(largestRing, COORDINATE_SCALE);

	if (largestRing.length < MINIMUM_RING_LENGTH) {
		return null;
	}

	// Hand the ring back in the winding the caller gave us.
	if (isClockwise) {
		largestRing.reverse();
	}

	return largestRing.map((point) => new THREE.Vector3(point.X, height, point.Y));
}

export { offsetPolygon };
